import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import { DICAS } from '@/conteudo/dicas';

const WHATSAPP_DUVIDA =
  'https://wa.me/5514996402835?text=Ol%C3%A1!%20Tenho%20uma%20d%C3%BAvida%20no%20Se%20Liga.';

interface DicaProps {
  id: string;
  eventoId?: number;
  className?: string;
  claro?: boolean;
}

// Balão "?" que abre uma folha com o título, o texto, o passo a passo e o
// próximo passo da dica. Some sozinho quando a id não existe no conteúdo.
export default function Dica({ id, eventoId, className = '', claro = false }: DicaProps) {
  const navigate = useNavigate();
  const params = useParams();
  const dica = DICAS[id];
  const [aberto, setAberto] = useState(false);
  const [jaViu, setJaViu] = useState(true);

  useEffect(() => {
    try {
      setJaViu(localStorage.getItem(`dica:${id}`) === '1');
    } catch {
      setJaViu(true);
    }
  }, [id]);

  if (!dica) return null;

  const idEvento = eventoId ?? numeroDoParametro(params.eventoId) ?? numeroDoParametro(params.id);

  function abrir() {
    setAberto(true);
    setJaViu(true);
    try {
      localStorage.setItem(`dica:${id}`, '1');
    } catch {
      /* sem storage: nada a guardar */
    }
  }

  function irProximo() {
    if (!dica?.proximo) return;
    const { para } = dica.proximo;
    setAberto(false);
    if (para.startsWith('#')) {
      window.dispatchEvent(new CustomEvent('seliga:dica', { detail: para.slice(1) }));
      return;
    }
    navigate(para.includes(':id') && idEvento ? para.replace(':id', String(idEvento)) : para);
  }

  return (
    <>
      <span className={`relative inline-flex h-8 w-8 shrink-0 ${className}`}>
        {!jaViu && (
          <span
            className={`pointer-events-none absolute inset-0 animate-pulse rounded-full ${
              claro ? 'bg-white/40' : 'bg-primary-300/60'
            }`}
            aria-hidden
          />
        )}
        <button
          type="button"
          onClick={abrir}
          aria-label="Como funciona"
          className={`relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 ${
            claro
              ? 'bg-white/15 text-white hover:bg-white/25 focus-visible:ring-white/50'
              : 'bg-primary-50 text-primary-600 hover:bg-primary-100 focus-visible:ring-primary-400'
          }`}
        >
          <i className="ri-question-line text-base" />
        </button>
      </span>

      <Modal
        aberto={aberto}
        titulo={dica.titulo}
        aoFechar={() => setAberto(false)}
        rodape={
          <div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Botao variante="fantasma" onClick={() => setAberto(false)}>
                Entendi
              </Botao>
              {dica.proximo && <Botao onClick={irProximo}>{dica.proximo.rotulo}</Botao>}
            </div>
            <a
              href={WHATSAPP_DUVIDA}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 whitespace-nowrap text-[13px] font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-whatsapp-line" />
              </span>
              Ainda com dúvida? Fale com o Se Liga
            </a>
          </div>
        }
      >
        <p className="text-[15px] leading-relaxed text-foreground-700">{dica.texto}</p>

        {dica.passos && dica.passos.length > 0 && (
          <div className="mt-5">
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-foreground-500">
              Passo a passo
            </h3>
            <ul className="mt-2 space-y-2.5">
              {dica.passos.map((passo, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[12px] font-bold text-primary-700">
                    {i + 1}
                  </span>
                  <span className="min-w-0 text-[15px] leading-relaxed text-foreground-800">
                    {passo}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </>
  );
}

function numeroDoParametro(valor?: string): number | undefined {
  if (!valor) return undefined;
  const n = Number(valor);
  return Number.isFinite(n) ? n : undefined;
}